/**
 * Persona lens — one lead line, weighted to who is asking.
 *
 * The same answer serves six personas; each one reads it for something
 * different. The lens adds a single blockquote line above the answer with
 * the figure that persona acts on, for the machine / component / site the
 * answer settled on (the kit's focus):
 *
 * | Persona | Lead line |
 * |---|---|
 * | Parts & Logistics (`parts`) | the parts position for the component — site store, DC, orders |
 * | Site Maintenance Planner (`planner`) | the next PM window on the machine |
 * | Fleet Reliability Lead (`rel`) | the precursor — what the data showed first |
 * | Regional Service Manager (`rsm`) | the customer and site — operator, contract, CSS |
 * | Field Service Technician (`tech`) | the procedure and isolation |
 * | Aftermarket Manager (`aftermarket`) | the A$ exposure |
 *
 * The operations intents (routing, coaching, driver tree, SPC, TARP, FMEA,
 * control plans) get their own lines from the reliability engines — see
 * {@link opsLens}: the planner gets the part to schedule, the reliability
 * lead the strongest piece of evidence, the service manager the operator
 * coaching (or the A$ when it is equipment), aftermarket the A$ exposure.
 *
 * It only speaks when it has a figure, and never repeats the answer's own
 * subject (no A$ lens on a cost answer, no parts lens on a stock answer).
 *
 * The line starts `> **… lens** — `; the regression gate strips it before
 * comparing an answer with the golden.
 *
 * Engine layer — async data loads, otherwise pure.
 */

import { BY_ID, COST_PER_MACHINE, HEALTH, MINES } from '../../data/core.ts';
import type { CustomerSatisfactionResponse } from '../../schema/person.ts';
import type { OemPersonaId } from '../../schema/persona.ts';
import { unpackTable } from '../columnar.ts';
import { coi, FLEET_RULES, fmt$, fmtk, PART_OF_COMP, procFor, renewals, totalCostOfInaction } from '../oem-engine.ts';
import {
  exposureByMachine,
  partFor,
  priorities,
  rootCause,
  trainingNeeds,
} from '../reliability/index.ts';
import { loadReliabilityCtx } from '../reliability/useReliability.ts';
import { ALERTS } from '../seed/alerts.ts';
import { siteRole } from '../seed/site-roles.ts';
import { loadOemDomain } from '../useOemData.ts';
import type { AnswerFocus } from './answer-kit.ts';
import { loadWorkOrders, mineLocations } from './tools.ts';

/** Intents that get a lens line. */
export const LENS_INTENTS = new Set([
  'risk_ranking',
  'prediction_confidence',
  'machine_history',
  'machine_lookup',
  'why_failed',
  'likely_causes',
  'telemetry',
  'change_next',
  'job_cost',
  'freight_decision',
  'incident_cost',
  'cost_of_inaction',
  'report_focus',
  'hotspots',
  'approvals_waiting',
  'most_expensive_job',
  // operations — see `opsLens`
  'route_observation',
  'operator_training',
  'shift_driver',
  'target_first',
  'spc_status',
  'tarp_status',
  'fmea_top',
  'control_plan',
]);

/** The operations intents; `opsLens` speaks for planner, rel, rsm and aftermarket on these. */
const OPS_INTENTS = new Set([
  'route_observation',
  'operator_training',
  'shift_driver',
  'target_first',
  'spc_status',
  'tarp_status',
  'fmea_top',
  'control_plan',
]);

/** Families whose answers already carry what a persona would lead with. */
const SKIP: Readonly<Partial<Record<OemPersonaId, readonly string[]>>> = {
  aftermarket: ['job_cost', 'freight_decision', 'incident_cost', 'cost_of_inaction', 'most_expensive_job', 'change_next', 'target_first'],
  parts: ['freight_decision'],
  rel: ['why_failed', 'incident_cost', 'route_observation'],
  rsm: ['target_first'],
};

const LABEL: Readonly<Record<OemPersonaId, string>> = {
  rel: 'Reliability',
  rsm: 'Service manager',
  tech: 'Technician',
  parts: 'Parts',
  planner: 'Planner',
  aftermarket: 'Aftermarket',
};

/** Marks a lens line, for the gate. */
export const LENS_LINE = /^> \*\*[A-Za-z ]+ lens\*\* — [^\n]*\n\n/;

const line = (p: OemPersonaId, text: string) => `> **${LABEL[p]} lens** — ${text}`;

export async function personaLens(
  personaId: OemPersonaId,
  intentId: string,
  focus: AnswerFocus,
): Promise<string | null> {
  if (!LENS_INTENTS.has(intentId) || SKIP[personaId]?.includes(intentId)) return null;
  const m = focus.machine && BY_ID[focus.machine] ? focus.machine : undefined;
  const mine = focus.mine ?? (m ? BY_ID[m].mine : undefined);
  const alert = m ? ALERTS.find((a) => a.machine === m) : undefined;
  const comp = focus.component ?? alert?.comp;
  if (OPS_INTENTS.has(intentId) && OPS_PERSONAS.has(personaId)) return opsLens(personaId, m, mine, comp);

  switch (personaId) {
    case 'aftermarket': {
      if (alert) {
        const c = coi(alert);
        if (c) return line(personaId, `${fmt$(c.delta)} at stake on ${alert.machine} if it fails before the planned job (${fmt$(c.planned)} planned vs ${fmt$(c.unplanned)}).`);
      }
      const cp = m ? COST_PER_MACHINE.find((c) => c.machine_id === m) : undefined;
      if (cp) return line(personaId, `${m} has cost ${fmt$(cp.amount_aud)} all-in over 18 months — A$${cp.cost_per_op_hour_aud} per operating hour.`);
      return line(personaId, `${fmtk(totalCostOfInaction())} is the cost of doing nothing across the ${ALERTS.length} open alerts.`);
    }
    case 'parts': {
      if (!comp || !mine) return null;
      const pns = PART_OF_COMP.find(([re]) => re.test(comp))?.[1];
      if (!pns?.length) return null;
      const parts = await loadOemDomain('parts');
      const [store, dc] = mineLocations(mine);
      const pn = pns[0];
      const at = (loc: string) => parts.inventory.find((i) => i.part_no === pn && i.location === loc)?.qty_on_hand ?? 0;
      const order = parts.orders.find((o) => o.part_no === pn && (!m || o.machine_id === m) && o.status !== 'Delivered');
      return line(
        personaId,
        `${pn}: ${at(store)} in the ${store}, ${at(dc)} at ${dc}${order ? `; ${order.order_no}: ${order.status.split(' · ')[0]}` : ''}.`,
      );
    }
    case 'planner': {
      if (!m) return null;
      const workOrders = await loadWorkOrders();
      const next = workOrders
        .filter((w) => w.machine_id === m && w.wo_type === 'Preventive' && w.status === 'Scheduled')
        .sort((a, b) => a.created.localeCompare(b.created))[0];
      const draft = workOrders.find((w) => w.machine_id === m && w.status.startsWith('Draft'));
      if (!next && !draft) return null;
      return line(
        personaId,
        `${next ? `next PM window on ${m}: ${next.pm_code ?? 'PM'} ${next.created.slice(0, 10)} (${next.crew}, ${next.labour_h} h)` : `no PM scheduled on ${m}`}` +
          `${draft ? `; ${draft.wo_id} is drafted for ${draft.component} and could ride on it` : ''}.`,
      );
    }
    case 'rel': {
      if (alert) return line(personaId, `precursor on ${alert.machine}: ${alert.why.split('. ')[0]}.`);
      const rule = comp ? FLEET_RULES.find(([r, , applies]) => new RegExp(comp.split(' ')[0], 'i').test(`${r} ${applies}`)) : undefined;
      if (rule) return line(personaId, `standing rule: ${rule[0]} (from ${rule[1]}).`);
      return null;
    }
    case 'rsm': {
      if (!mine) return null;
      const [service, people] = await Promise.all([loadOemDomain('service'), loadOemDomain('people')]);
      const site = MINES[mine];
      const contract = renewals({ contracts: service.mx_contracts }).find((c) =>
        c.customer.toLowerCase().startsWith(site.name.split(' ')[0].toLowerCase()),
      );
      const css = unpackTable<CustomerSatisfactionResponse>(people.css).filter((r) =>
        r.Site.toLowerCase().startsWith(site.name.split(' ')[0].toLowerCase()),
      );
      const avg = css.length ? (css.reduce((a, r) => a + r.Overall, 0) / css.length).toFixed(1) : null;
      return line(
        personaId,
        `${site.name} — ${site.operator_model}` +
          `${contract ? `; contract ${contract.contractnum} renews in ${contract.days} days` : ''}` +
          `${avg ? `; CSS ${avg}/5 from ${css.length} responses` : ''}.`,
      );
    }
    case 'tech': {
      if (!comp) return null;
      const docIndex = await loadOemDomain('doc-index');
      const proc = procFor(comp, { documents: docIndex.docs });
      // `procFor` falls back to the general PM checklist; that is not a procedure for this job.
      if (!proc || proc.file.startsWith('PM-MER')) return null;
      return line(personaId, `procedure for ${comp}: ${proc.title.split(' — ')[0]}${/LOTO/.test(proc.file) ? '' : '; isolate under LOTO-UG-003 first'}.`);
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Operations lens
// ---------------------------------------------------------------------------

const OPS_PERSONAS = new Set<OemPersonaId>(['planner', 'rel', 'rsm', 'aftermarket']);

/**
 * The lead line on an operations answer, from the reliability engines:
 * planner → the part to schedule; rel → the strongest evidence; rsm → the
 * operator coaching to raise with the customer (the A$ when it is not
 * behaviour); aftermarket → the A$ exposure.
 */
async function opsLens(
  personaId: OemPersonaId,
  m: string | undefined,
  mine: AnswerFocus['mine'],
  comp: string | undefined,
): Promise<string | null> {
  const ctx = await loadReliabilityCtx();
  const register = m && comp ? HEALTH_COMPONENTS(m).find((c) => c === comp) : undefined;
  const exposureLine = (): string | null => {
    const all = exposureByMachine(ctx);
    const x = m ? all.find((e) => e.machine === m) : undefined;
    if (x) {
      const rank = priorities(ctx, 100).findIndex((p) => p.machine === m) + 1;
      return `${fmtk(x.valueAud)} exposure on ${m}${rank ? ` — #${rank} on the target-first list` : ''}; biggest driver: ${x.drivers[0].text.split(';')[0]}.`;
    }
    const scoped = mine ? all.filter((e) => e.mine === mine) : all;
    if (!scoped.length) return null;
    const total = scoped.reduce((a, e) => a + e.valueAud, 0);
    return `${fmtk(total)} exposure across ${scoped.length} machine${scoped.length === 1 ? '' : 's'}${mine ? ` at ${MINES[mine].name}` : ''}; ${scoped[0].machine} carries the most (${fmtk(scoped[0].valueAud)}).`;
  };
  switch (personaId) {
    case 'planner': {
      if (!m || !register) return null;
      const part = partFor(m, register, ctx);
      if (!part) return null;
      const stock = part.siteStock === null ? '' : part.siteStock > 0 ? `${part.siteStock} on the ${BY_ID[m].mine} shelf` : `none on the ${BY_ID[m].mine} shelf`;
      return line(
        personaId,
        `schedule ${part.partNo}${part.description ? ` (${part.description})` : ''} for ${m}` +
          `${stock ? ` — ${stock}` : ''}${part.leadTimeDays !== null ? `, ${part.leadTimeDays}-day lead time` : ''}.`,
      );
    }
    case 'rel': {
      if (!m || !register) return null;
      const f = rootCause(m, register, ctx);
      const ev = f.evidence[0];
      return ev ? line(personaId, `${f.cause} call on ${m} (${Math.round(f.confidence * 100)}%) — ${ev}${/[."]$/.test(ev) ? '' : '.'}`) : null;
    }
    case 'rsm': {
      if (m && register) {
        const f = rootCause(m, register, ctx);
        if ((f.cause === 'operator' || f.cause === 'mixed') && f.topOperator) {
          const sup = siteRole(f.mine, 'Operating Supervisor');
          return line(
            personaId,
            `raise coaching for ${f.topOperator.name} (crew ${f.topOperator.crew}) with ${sup.personName}, ${MINES[f.mine].name}'s Operating Supervisor — ` +
              `${Math.round(f.topOperator.eventShare * 100)}% of ${m}'s events on ${Math.round(f.topOperator.hoursShare * 100)}% of the hours.`,
          );
        }
      }
      if (!m && mine) {
        const needs = trainingNeeds(ctx.attribution).filter((n) => n.mine === mine);
        if (needs.length) {
          return line(personaId, `${needs.length} operator${needs.length === 1 ? '' : 's'} to raise with ${siteRole(mine, 'Operating Supervisor').personName} at ${MINES[mine].name}; ${needs[0].name} leads at ${needs[0].ratio.toFixed(1)}× peers.`);
        }
      }
      const x = exposureLine();
      return x ? line(personaId, x) : null;
    }
    case 'aftermarket': {
      const x = exposureLine();
      return x ? line(personaId, x) : null;
    }
    default:
      return null;
  }
}

/** A machine's register component names. */
const HEALTH_COMPONENTS = (m: string): string[] => HEALTH.filter((h) => h.machine_id === m).map((h) => h.component);
